import {
  ARCANIST_DECAY_SECONDS, ARCANIST_HOLD_SECONDS, MENTALIST_FLAT_LEY,
  PP_REGEN_AT_FULL_LEY,
} from './constants.ts';
import { CLASSES } from './classes.ts';
import { clamp, dist, dist2d, falloff } from './math.ts';
import type { Arena } from './arena.ts';
import type { PlayerState } from './player.ts';

/**
 * Ley is the purple bar: the *rate* at which power returns, not how much you
 * hold. Each class draws it from somewhere different, and that difference is
 * the whole reason position matters differently depending on who you picked.
 *
 *   Cleric     own shrine proximity, and flatly zero inside an enemy shrine
 *   Magician   proximity to a pool biased to your own faction
 *   Mentalist  a flat value everywhere, needing nothing from the map
 *   Arcanist   charges to full in any pool regardless of owner, holds twenty
 *              seconds after leaving, then falls off a cliff
 *
 * Over all of it sits one rule about standing in water you do not own — see
 * `stalledInPool`, which is the Arcanist's whole reason to exist.
 */
export function computeLey(p: PlayerState, arena: Arena, dt: number): number {
  const def = CLASSES[p.cls];

  // The Arcanist charges even while stalled, so the age has to keep running
  // before the early return below can take it away.
  if (stalledInPool(p, arena)) return 0;

  switch (def.leySource) {
    case 'own-shrine': {
      // Standing in somebody else's shrine cuts a Cleric off completely.
      for (const s of arena.shrines) {
        if (s.faction !== p.faction && dist2d(p.pos, s.pos) <= s.innerRadius) return 0;
      }
      const own = arena.shrines.find((s) => s.faction === p.faction);
      if (!own || own.conquered) return 0;
      return falloff(dist(p.pos, own.pos), own.regenRange, own.innerRadius);
    }

    case 'friendly-pool': {
      let best = 0;
      for (const pool of arena.pools) {
        if (pool.owner !== p.faction) continue;
        best = Math.max(best, falloff(dist(p.pos, pool.pos), pool.regenRange, pool.innerRadius));
      }
      return best;
    }

    case 'self':
      return MENTALIST_FLAT_LEY;

    case 'any-pool-charge': {
      const standingIn = arena.pools.some(
        (pool) => dist2d(p.pos, pool.pos) <= pool.innerRadius,
      );
      if (standingIn) {
        p.poolChargeAge = 0;
        return 1;
      }
      p.poolChargeAge += dt;
      if (p.poolChargeAge <= ARCANIST_HOLD_SECONDS) return 1;
      const past = p.poolChargeAge - ARCANIST_HOLD_SECONDS;
      return clamp(1 - past / ARCANIST_DECAY_SECONDS, 0, 1);
    }
  }
}

/**
 * Whether standing here has cut this mage off from their power.
 *
 * Biasing a pool means wading into it and waiting, and the original made that a
 * real decision by taking your regeneration away while you did it: an Eldritch
 * was the only profession that could cast while standing in a pool not fully
 * biased to its team. That single line is most of what the class was for.
 *
 * Keyed on `canBiasPools`, which is exactly the right question and reads as its
 * own argument — the professions that can claim a pool are the ones that pay for
 * the attempt, and the one that cannot claim it is the one that fights in it.
 * The Arcanist is not a special case here so much as the case this rule creates.
 *
 * "Not fully biased" means what it says: a pool your team owns but has not
 * finished re-securing after somebody contested it is still not yours, and a
 * Magician standing in one goes dry until the meter is full again. That is the
 * intended pressure. Losing ground on a pool should cost more than the pool.
 */
export function stalledInPool(p: PlayerState, arena: Arena): boolean {
  if (!CLASSES[p.cls].canBiasPools) return false;

  return arena.pools.some((pool) => {
    if (dist2d(p.pos, pool.pos) > pool.innerRadius) return false;
    return !(pool.owner === p.faction && pool.progress >= 1);
  });
}

/** Power regained per second at the player's current ley. */
export function ppPerSecond(p: PlayerState): number {
  return p.ppMax * p.ley * PP_REGEN_AT_FULL_LEY;
}
